import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { MediaStorageConfigDTO, MediaStorageConfigUpdateParams } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid media storage config': '存储桶配置无效，请检查域名格式和参数内容',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

const EMPTY_FORM: MediaStorageConfigUpdateParams = {
  bucket: '',
  region: '',
  public_domain: '',
  secret_id: '',
  secret_key: '',
};

function dtoToForm(dto: MediaStorageConfigDTO): MediaStorageConfigUpdateParams {
  return {
    bucket: dto.bucket ?? '',
    region: dto.region ?? '',
    public_domain: dto.public_domain ?? '',
    secret_id: dto.secret_id ?? '',
    secret_key: '',
  };
}

export function useMediaStorageConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<MediaStorageConfigDTO | null>(null);
  const [form, setForm] = useState<MediaStorageConfigUpdateParams>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getMediaStorageConfig();
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = useCallback(<K extends keyof MediaStorageConfigUpdateParams>(key: K, value: MediaStorageConfigUpdateParams[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const ready = useMemo(() => {
    const bucketReady = form.bucket.trim() !== '' || Boolean(data?.bucket_configured);
    const regionReady = form.region.trim() !== '' || Boolean(data?.region_configured);
    const publicDomainReady = form.public_domain.trim() !== '' || Boolean(data?.public_domain_configured);
    const secretIDReady = form.secret_id.trim() !== '' || Boolean(data?.secret_id_configured);
    const secretKeyReady = form.secret_key.trim() !== '' || Boolean(data?.secret_key_configured);
    return bucketReady && regionReady && publicDomainReady && secretIDReady && secretKeyReady;
  }, [data, form]);

  const warnings = useMemo(() => {
    const nextWarnings: string[] = [];
    if (!ready) {
      nextWarnings.push('当前图片存储配置不完整，上传签名接口将无法正常签发。');
    }
    if (form.public_domain.trim() !== '' && !/^https?:\/\//i.test(form.public_domain.trim())) {
      nextWarnings.push('公网访问域名必须以 http:// 或 https:// 开头。');
    }
    return nextWarnings;
  }, [form.public_domain, ready]);

  const save = useCallback(async () => {
    const publicDomain = form.public_domain.trim();
    if (publicDomain !== '' && !/^https?:\/\//i.test(publicDomain)) {
      addToast('error', '公网访问域名必须以 http:// 或 https:// 开头；留空则继续使用服务端环境变量');
      return false;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateMediaStorageConfig({
        bucket: form.bucket.trim(),
        region: form.region.trim(),
        public_domain: publicDomain,
        secret_id: form.secret_id.trim(),
        secret_key: form.secret_key.trim(),
      });
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', '存储桶配置已保存，新的上传签名请求会立即使用最新配置');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form]);

  return {
    data,
    form,
    loading,
    saving,
    error,
    ready,
    warnings,
    updateField,
    save,
    reload: load,
  };
}
